import { Component, OnInit, ViewChild } from '@angular/core';
import { CompanySettingsComponent } from './tabs/company-settings/company-settings.component';
import { Router, ActivatedRoute } from '@angular/router';
import { SessionService } from '@src/app/shared/services/session.service';

interface SystemSettingsTab {
  label: string;
  route: string;
}

@Component({
    selector: 'app-system-settings',
    templateUrl: './system-settings.component.html',
    styleUrls: ['./system-settings.component.scss'],
    host: {
        class: 'app-system-settings'
    },
    standalone: false
})
export class SystemSettingsComponent implements OnInit {

  @ViewChild(CompanySettingsComponent)
  private companySettingsComponent?: CompanySettingsComponent;

  activeTabRoute: string = 'company';
  activeBrandingTabIndex: number = 0;

  isSuperAdmin: boolean = false;
  isAdmin: boolean = false;

  // aside nav
  tabs: SystemSettingsTab[] = [];

  private readonly superAdminTabs: SystemSettingsTab[] = [
    { label: 'SYSTEM_SETTINGS.TABS.COMPANY', route: 'company' },
    { label: 'SYSTEM_SETTINGS.TABS.BRANDS', route: 'brands' },
    { label: 'SYSTEM_SETTINGS.FEATURES_NAV_SETTINGS_FEATURES', route: 'features' },
    { label: 'SYSTEM_SETTINGS.TABS.NOTICES', route: 'notices' },
    { label: 'SYSTEM_SETTINGS.FEATURES_NAV_SETTINGS_TABLES_COLUMNS', route: 'tables' },
    { label: 'SYSTEM_SETTINGS.FEATURES_NAV_SETTINGS_EXPORT_COLUMNS', route: 'export' },
    { label: 'SYSTEM_SETTINGS.FEATURES_NAV_SETTINGS_SPECIFICATIONS', route: 'specification' },
    { label: 'SYSTEM_SETTINGS.FEATURES_NAV_SETTINGS_PATTERNS', route: 'pattern' },
    { label: 'SYSTEM_SETTINGS.TABS.ROLES', route: 'roles' },
    { label: 'SYSTEM_SETTINGS.TABS.SERIALIZATION', route: 'serialisation' },
    { label: 'SYSTEM_SETTINGS.TABS.MACHINE_CLIENTS', route: 'machine-clients' },
    // { label: 'SYSTEM_SETTINGS.TABS.TRANSLATIONS', route: 'translations' },
    { label: 'SYSTEM_SETTINGS.TABS.INTEGRATIONS', route: 'integrations' },
    { label: 'SYSTEM_SETTINGS.TABS.BRANDING', route: 'branding' }
  ];

  private readonly adminTabs: SystemSettingsTab[] = [
    { label: 'SYSTEM_SETTINGS.TABS.ROLES', route: 'roles' },
  ];

  constructor(
    private route: ActivatedRoute,
    public session: SessionService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.init();
  }

  private async init(): Promise<void> {
    // only superadmin is allowed to manage system settings
    this.session.isSuperAdmin$().subscribe((isSuperAdmin) => {
      // if (!isSuperAdmin) this.router.navigateByUrl('/');
      this.isSuperAdmin = isSuperAdmin;
      this.setAvailableTabs();
    });

    this.session.isAdmin$().subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
      this.setAvailableTabs();
    });

    this.route.queryParams.subscribe(params => {
      this.activeTabRoute = this.getAvailableTabRoute(params['tab']);
    });
  }

  openCreateCompanyModal(): void {

    // bugfix: angular needs a sec to render the company settings component before @viewChild to access it
    if (this.activeTabRoute !== 'company') {
      this.selectTab('company');
      setTimeout(() => {
        this.companySettingsComponent?.openCreateCompanyModal();
      });
      return;
    }

    this.companySettingsComponent?.openCreateCompanyModal();
  }

  selectTab(route: string): void {
    if (this.activeTabRoute === route) {
      return;
    }

    this.activeTabRoute = route;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: route },
      queryParamsHandling: 'merge'
    });
  }

  trackByTabRoute(_index: number, tab: SystemSettingsTab): string {
    return tab.route;
  }

  onBrandingTabChange(index: number): void {
    this.activeBrandingTabIndex = index;

    const tabRoute = index === 1 ? 'digital-card' : 'theme';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabRoute },
      queryParamsHandling: 'merge'
    });
  }

  private setAvailableTabs(): void {
    if (this.isSuperAdmin) {
      this.tabs = this.superAdminTabs;
    } else if (this.isAdmin) {
      this.tabs = this.adminTabs;
    } else {
      this.tabs = [];
    }

    this.activeTabRoute = this.getAvailableTabRoute(this.route.snapshot.queryParams['tab']);
  }

  private getAvailableTabRoute(route: string | undefined): string {
    const requestedRoute = this.getTabRouteAlias(route);
    const requestedTab = this.tabs.find(tab => tab.route === requestedRoute);

    if (requestedTab) {
      return requestedTab.route;
    }

    return this.tabs[0]?.route ?? this.activeTabRoute;
  }

  private getTabRouteAlias(route: string | undefined): string | undefined {
    if (route === 'theme' || route === 'digital-card') {
      this.activeBrandingTabIndex = route === 'digital-card' ? 1 : 0;

      return 'branding';
    }

    return route;
  }
}
